import { Money } from 'shared/types/product/Money';
import { Log } from '../errorLogger';
import { StringHelpers } from '../stringHelpers';

export interface LocaleConfig {
  symbol: string;
  position: 'start' | 'end';
  space: boolean;
  useNarrowSpace: boolean;
  /** thousands separator, e.g. ' ', '.', ',' or '\u202F' (narrow no-break space) */
  groupingSeparator: string;
  decimalSeparator: ',' | '.';
  /** currency this locale normally prices in; a different requested currency falls back to CURRENCY_SYMBOL_FALLBACK */
  defaultCurrency: string;
  forceZeroDecimals?: boolean;
}

const NARROW_SPACE = '\u202F';

/** used when the requested currencyCode doesn't match a locale's defaultCurrency */
const CURRENCY_SYMBOL_FALLBACK: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CZK: 'Kč',
  DKK: 'kr.',
  HUF: 'Ft',
  PLN: 'zł',
  RON: 'lei',
  SEK: 'kr',
  CAD: '$',
  BGN: 'лв.',
};

export const CUSTOM_LOCALE_MAP: Record<string, LocaleConfig> = {
  'en-us': {
    symbol: '$',
    position: 'start',
    space: false,
    useNarrowSpace: false,
    groupingSeparator: ',',
    decimalSeparator: '.',
    defaultCurrency: 'USD',
  },
  'de-at': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'nl-be': {
    symbol: '€',
    position: 'start',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'fr-be': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: true,
    groupingSeparator: NARROW_SPACE,
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'bg-bg': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'hr-hr': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'el-cy': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'tr-cy': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'cs-cz': {
    symbol: 'Kč',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'CZK',
  },
  'da-dk': {
    symbol: 'kr.',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'DKK',
  },
  'et-ee': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'fi-fi': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'fr-fr': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: true,
    groupingSeparator: NARROW_SPACE,
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'de-de': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'el-gr': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'hu-hu': {
    symbol: 'Ft',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'HUF',
    forceZeroDecimals: true,
  },
  'en-ie': {
    symbol: '€',
    position: 'start',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ',',
    decimalSeparator: '.',
    defaultCurrency: 'EUR',
  },
  'it-it': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'lv-lv': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'lt-lt': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'lb-lu': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'fr-lu': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: true,
    groupingSeparator: NARROW_SPACE,
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'de-lu': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'mt-mt': {
    symbol: '€',
    position: 'start',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ',',
    decimalSeparator: '.',
    defaultCurrency: 'EUR',
  },
  'en-mt': {
    symbol: '€',
    position: 'start',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ',',
    decimalSeparator: '.',
    defaultCurrency: 'EUR',
  },
  'nl-nl': {
    symbol: '€',
    position: 'start',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'pl-pl': {
    symbol: 'zł',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'PLN',
  },
  'pt-pt': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'ro-ro': {
    symbol: 'lei',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'RON',
  },
  'sk-sk': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'sl-si': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'es-es': {
    symbol: '€',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: '.',
    decimalSeparator: ',',
    defaultCurrency: 'EUR',
  },
  'sv-se': {
    symbol: 'kr',
    position: 'end',
    space: true,
    useNarrowSpace: false,
    groupingSeparator: ' ',
    decimalSeparator: ',',
    defaultCurrency: 'SEK',
  },
  'en-gb': {
    symbol: '£',
    position: 'start',
    space: false,
    useNarrowSpace: false,
    groupingSeparator: ',',
    decimalSeparator: '.',
    defaultCurrency: 'GBP',
  },
};
export class CurrencyHelpers {
  private static readonly formatNumberForCurrency = function (
    costInCents: number,
    locale?: string,
    currencyCode = 'USD',
    fractionDigits = 0,
  ) {
    return CurrencyHelpers.formatMoneyCurrency(
      {
        centAmount: costInCents,
        currencyCode: currencyCode,
        fractionDigits: fractionDigits,
      },
      locale,
    );
  };

  private static readonly formatStringForCurrency = function (
    costInCents: string,
    locale?: string,
    currencyCode = 'EUR',
    fractionDigits = 0,
  ) {
    if (!StringHelpers.isNumeric(costInCents)) {
      Log.error(new Error(`Value (${costInCents}) passed for currency formatting cannot be parsed to a number`));
      return '';
    }
    return CurrencyHelpers.formatNumberForCurrency(
      Number.parseInt(costInCents, 10),
      locale,
      currencyCode,
      fractionDigits,
    );
  };

  private static readonly LOCALE_LOOKUP: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    nl: 'nl-NL',
    fr: 'fr-FR',
    el: 'el-GR',
    bg: 'bg-BG',
    hr: 'hr-HR',
    cs: 'cs-CZ',
    da: 'da-DK',
    et: 'et-EE',
    fi: 'fi-FI',
    hu: 'hu-HU',
    it: 'it-IT',
    lv: 'lv-LV',
    lt: 'lt-LT',
    lb: 'lb-LU',
    mt: 'mt-MT',
    pl: 'pl-PL',
    pt: 'pt-PT',
    ro: 'ro-RO',
    sk: 'sk-SK',
    sl: 'sl-SL',
    es: 'es-ES',
    sv: 'sv-SE',
    tr: 'tr-CY',
    'en-us': 'en-US',
    'de-at': 'de-AT',
    'nl-be': 'nl-BE',
    'fr-be': 'fr-BE',
    'bg-bg': 'bg-BG',
    'hr-hr': 'hr-HR',
    'el-cy': 'el-CY',
    'tr-cy': 'tr-CY',
    'cs-cz': 'cs-CZ',
    'da-dk': 'da-DK',
    'et-ee': 'et-EE',
    'fi-fi': 'fi-FI',
    'fr-fr': 'fr-FR',
    'de-de': 'de-DE',
    'el-gr': 'el-GR',
    'hu-hu': 'hu-HU',
    'en-ie': 'en-IE',
    'it-it': 'it-IT',
    'lv-lv': 'lv-LV',
    'lt-lt': 'lt-LT',
    'lb-lu': 'lb-LU',
    'fr-lu': 'fr-LU',
    'de-lu': 'de-LU',
    'mt-mt': 'mt-MT',
    'en-mt': 'en-MT',
    'nl-nl': 'nl-NL',
    'pl-pl': 'pl-PL',
    'pt-pt': 'pt-PT',
    'ro-ro': 'ro-RO',
    'sk-sk': 'sk-SK',
    'sl-si': 'sl-SI',
    'es-es': 'es-ES',
    'sv-se': 'sv-SE',
    'en-gb': 'en-GB',
    'en-ca': 'en-CA',
    'fr-ca': 'fr-CA',
  };

  private static readonly getLocaleFromShortenedLocale = function (locale: string): string {
    if (!locale) return 'en-US';

    const key = locale.toLowerCase().trim();

    return CurrencyHelpers.LOCALE_LOOKUP[key] ?? locale;
  };

  private static readonly groupThousands = function (digits: string, separator: string): string {
    const groups: string[] = [];
    let end = digits.length;
    while (end > 3) {
      groups.unshift(digits.slice(end - 3, end));
      end -= 3;
    }
    groups.unshift(digits.slice(0, end));
    return groups.join(separator);
  };

  /**
   * Builds a grouped/decimal number string directly from integer cents, avoiding
   * floating-point rounding drift and native ICU grouping differences entirely.
   */
  private static readonly formatGroupedNumber = function (
    centAmount: number,
    fractionDigits: number,
    decimals: number,
    groupingSeparator: string,
    decimalSeparator: string,
    truncate: boolean,
  ): string {
    const divisor = 10 ** (fractionDigits - decimals);
    const scaledCents = truncate ? Math.trunc(centAmount / divisor) : Math.round(centAmount / divisor);
    const isNegative = scaledCents < 0;
    const digits = Math.abs(scaledCents)
      .toString()
      .padStart(decimals + 1, '0');
    const integerPart = decimals > 0 ? digits.slice(0, -decimals) : digits;
    const decimalPart = decimals > 0 ? digits.slice(-decimals) : '';
    const groupedInteger = CurrencyHelpers.groupThousands(integerPart, groupingSeparator);
    const numberString = decimals > 0 ? `${groupedInteger}${decimalSeparator}${decimalPart}` : groupedInteger;
    return `${isNegative ? '-' : ''}${numberString}`;
  };

  // Canada shows a "CAD"/"CA" currency qualifier alongside the $ symbol, which doesn't fit the generic symbol+position model
  private static readonly formatCanadianCurrency = function (
    normalizedLocale: string,
    centAmount: number,
    fractionDigits: number,
  ): string | undefined {
    if (normalizedLocale === 'en-ca') {
      const numberString = CurrencyHelpers.formatGroupedNumber(
        centAmount,
        fractionDigits,
        fractionDigits,
        ',',
        '.',
        false,
      );
      return `CAD$${numberString}`;
    }
    if (normalizedLocale === 'fr-ca') {
      const numberString = CurrencyHelpers.formatGroupedNumber(
        centAmount,
        fractionDigits,
        fractionDigits,
        NARROW_SPACE,
        ',',
        false,
      );
      return `${numberString} $ CA`;
    }
    return undefined;
  };

  private static readonly formatFromLocaleConfig = function (
    config: LocaleConfig,
    currency: string,
    centAmount: number,
    fractionDigits: number,
  ): string {
    const decimals = config.forceZeroDecimals ? 0 : fractionDigits;
    const numberString = CurrencyHelpers.formatGroupedNumber(
      centAmount,
      fractionDigits,
      decimals,
      config.groupingSeparator,
      config.decimalSeparator,
      !!config.forceZeroDecimals,
    );
    const symbol =
      currency === config.defaultCurrency ? config.symbol : (CURRENCY_SYMBOL_FALLBACK[currency] ?? currency);
    let gap = '';
    if (config.space) {
      gap = config.useNarrowSpace ? NARROW_SPACE : ' ';
    }
    return config.position === 'start' ? `${symbol}${gap}${numberString}` : `${numberString}${gap}${symbol}`;
  };

  private static readonly formatMoneyCurrency = function (price: Money, locale?: string) {
    const targetLocale = CurrencyHelpers.getLocaleFromShortenedLocale(locale as string);
    const currency = price?.currencyCode ?? 'USD';
    const fractionDigits = price?.fractionDigits ?? 2;
    const centAmount = price?.centAmount ?? 0;
    const normalizedLocale = targetLocale ? targetLocale.toLowerCase() : 'en-us';

    if (currency === 'CAD') {
      const canadianResult = CurrencyHelpers.formatCanadianCurrency(normalizedLocale, centAmount, fractionDigits);
      if (canadianResult !== undefined) {
        return canadianResult;
      }
    }

    const config = CUSTOM_LOCALE_MAP[normalizedLocale];

    if (config) {
      return CurrencyHelpers.formatFromLocaleConfig(config, currency, centAmount, fractionDigits);
    }

    // fall back to native Intl for any locale outside the explicitly supported set above
    return new Intl.NumberFormat(targetLocale || 'en-US', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(centAmount / 10 ** fractionDigits);
  };

  /**
   * formatForCurrency formats a string of number into a cost representation. If
   * passing a string it must be numeric only.
   */
  static readonly formatForCurrency = (
    costInCents: string | number | Money,
    locale?: string,
    currencyCode = 'EUR',
    fractionDigits = 0,
  ) =>
    typeof costInCents === 'string'
      ? CurrencyHelpers.formatStringForCurrency(costInCents, locale, currencyCode, fractionDigits)
      : typeof costInCents === 'number'
        ? CurrencyHelpers.formatNumberForCurrency(costInCents, locale, currencyCode, fractionDigits)
        : CurrencyHelpers.formatMoneyCurrency(costInCents, locale);
  static readonly addCurrency: (value1?: Money, value2?: Money) => Money = (value1?: Money, value2?: Money) => {
    if (!value1) value1 = { centAmount: 0 };
    if (!value2) value2 = { centAmount: 0 };
    if (value1.fractionDigits !== value2.fractionDigits && value1.fractionDigits && value2.fractionDigits) {
      Log.warn(
        new Error(
          `Money with different fraction codes passed to addCurrency, value returned will be innacurate. ` +
            `Value 1: ${value1.fractionDigits}, value 2: ${value2.fractionDigits}`,
        ),
      );
    }
    if (value1.currencyCode !== value2.currencyCode && value1.currencyCode && value2.currencyCode) {
      Log.warn(
        new Error(
          `Money with different currency codes passed to addCurrency, value returned will be innacurate. ` +
            `Value 1: ${value1.currencyCode}, value 2: ${value2.currencyCode}`,
        ),
      );
    }
    return {
      fractionDigits: value1.fractionDigits ?? value2.fractionDigits,
      centAmount: (value1.centAmount as number) + (value2.centAmount as number),
      currencyCode: value1.currencyCode ?? value2.currencyCode,
    };
  };

  static readonly localizedFormatForCurreny = (amount: string | number, locale: string, currencyCode = 'USD') => {
    const amountInNumericFormat = typeof amount === 'number' ? amount : Number(amount);
    const formatter = new Intl.NumberFormat(CurrencyHelpers.getLocaleFromShortenedLocale(locale), {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 10,
      trailingZeroDisplay: 'stripIfInteger',
    });

    return formatter.format(amountInNumericFormat);
  };

  static readonly subtractCurrency: (value1: Money, value2: Money) => Money = (value1: Money, value2: Money) => {
    if (value1.fractionDigits !== value2.fractionDigits) {
      Log.warn(
        new Error(
          `Money with different fraction codes passed to addCurrency, value returned will be innacurate. ` +
            `Value 1: ${value1.fractionDigits}, value 2: ${value2.fractionDigits}`,
        ),
      );
    }
    if (value1.currencyCode !== value2.currencyCode) {
      Log.warn(
        new Error(
          `Money with different currency codes passed to addCurrency, value returned will be innacurate. ` +
            `Value 1: ${value1.currencyCode}, value 2: ${value2.currencyCode}`,
        ),
      );
    }
    return {
      fractionDigits: value1.fractionDigits ?? value2.fractionDigits,
      centAmount: (value1.centAmount as number) - (value2.centAmount as number),
      currencyCode: value1.currencyCode ?? value2.currencyCode,
    };
  };

  static readonly multiplyCurrency: (value: Money, numberOfItems: number) => Money = (
    value: Money,
    numberOfItems: number,
  ) => ({
    fractionDigits: value.fractionDigits,
    centAmount: (value.centAmount as number) * numberOfItems,
    currencyCode: value.currencyCode,
  });
}
